import {remCalc} from '_themeUtils'

const productColorSelector = {
    ProductColorSelector: {
        baseStyle: ({moreColor}) => ({
            // ─── Drawer content ───────────────────────────────────────────
            container: {
                gap: 'spacing.10',
            },
            drawerTitle: {
                textTransform: 'none',
                textStyle: 'displayXs',
                fontWeight: 500,

                _firstLetter: {
                    textTransform: 'capitalize',
                },
            },
            colorsWrapper: {
                w: '100%',
                // Color slots are stacked flush (Figma node 58243-32958: 48px rows with a bottom divider,
                // no gap). The previous 8px desktop gap (md: 0.5rem) left a white strip between a filled
                // (hover/selected) slot and the previous row's divider — same issue fixed for the size
                // selector. Force it to 0 on every breakpoint.
                gap: 0,
            },
            // ─── Color slot (Figma node 58243-32958: Default / Hover / Selected) ──────────
            // The whole row is the interactive "Color slot". Per design:
            //  - Default:  white bg, bottom divider #d2d2d2, no ring on the swatch.
            //  - Hover:    bg #f2f2f2 (background/darker), bottom border #262626, swatch ring #d2d2d2.
            //  - Selected: same bg + darker border as hover; the swatch ring stays on (driven by the
            //              ColorCircle `selected` prop) and the gap widens to 15px.
            // The swatch ring on hover is revealed via the `.cc-color-circle__ring` marker so it doesn't
            // require a JS hover state (the ColorCircle atom keeps the ring transparent until selected).
            colorItemWrapper: {
                w: '100%',
                justifyContent: 'space-between',
                borderBottom: '1px solid',
                borderColor: 'color.border.grey.400',
                _hover: {
                    bg: 'color.background.grey.200',
                    borderColor: 'color.border.black',
                    '.cc-color-circle__ring': {
                        borderColor: 'color.border.grey.400',
                    },
                },
            },
            colorItemWrapperSelected: {
                bg: 'color.background.grey.200',
                borderColor: 'color.border.black',
            },
            colorItemButton: {
                w: 'auto',
                flex: '1 1',
                textAlign: 'left',
                // Figma color slot: 10px gap between the swatch and the label (Default/Hover).
                gap: 'spacing.2.5',
                // Divider now lives on the row (colorItemWrapper) so hover/selected darken the full-width
                // slot border consistently with Figma; keep the button borderless.
            },
            colorItemButtonSelected: {
                pointerEvents: 'none',
                // Keep the SAME swatch↔label gap (10px) as the not-selected rows so the label stays
                // aligned when the swatch gains its selected outer stroke. Figma (node 58243-32958) shows
                // a 15px gap for Selected — treated as a design inconsistency here, since the swatch box
                // and its inner dot are fixed and the label should not shift on selection.
                gap: 'spacing.2.5',
            },
            colorItemLabel: {
                w: '100%',
                textStyle: 'paragraphSm',
                fontWeight: 'normal',
            },

            // ─── Inline swatches row (PDP) ────────────────────────────────
            swatchesRow: {
                w: '100%',
                justifyContent: 'space-between',
                alignItems: 'center',
                py: moreColor ? 0 : remCalc(15),
            },
            swatchesLabel: {
                textStyle: 'paragraphSm',
                fontWeight: 'normal',
                flexShrink: 0,
            },
            swatchesList: {
                // Figma node 58120-18431: the swatch group is `flex gap-[10px]`, so the spacing between
                // each colour dot AND between the last dot and the "+" icon is 10px (spacing.2.5), not 12px.
                gap: 'spacing.2.5',
                alignItems: 'center',
            },
            // Figma "Color dots" outer box (24px). Two-ring visual (inner dark dot + grey outer ring
            // on selection) is handled by the ColorCircle atom via its `selected` prop.
            swatchItem: {
                position: 'relative',
                w: remCalc(24),
                h: remCalc(24),
                flexShrink: 0,
                p: 0,
            },
            swatchMoreCta: {
                textStyle: 'paragraphXs',
                fontWeight: 400,
                whiteSpace: 'nowrap',
                // Figma desktop (node 5685-8118): the gap between the colour dots is 10px, but the gap
                // between the last dot and the "+ N colori" text is 15px. The row's flex gap already
                // provides 10px, so add 5px here to reach 15px only before this CTA.
                ml: remCalc(5),
            },
            // Mobile-only "+" icon CTA. The shared Button baseStyle applies width:100% and minH:48px; left
            // unreset, the icon button would stretch and add vertical/horizontal space that breaks the
            // 10px gap to the last colour dot. Constrain it to the icon box (Figma node 58120-18452: 24px)
            // with no extra padding/min size so the "+" sits exactly 10px from the last swatch.
            swatchMoreIconCta: {
                w: remCalc(24),
                minW: remCalc(24),
                h: remCalc(24),
                minH: remCalc(24),
                p: 0,
                flexShrink: 0,
            },
        }),
    },
}

export default productColorSelector
